import { supabase } from '../config/supabase';

export type HistoricalPlayedEvent = {
  match_event_id: string;
  player_id: string;
  league_configuration_position_id: string | null;
  participation_event_reason?: string | null;
};
export type HistoricalLineupEntry = {
  match_event_id: string;
  player_id: string;
  league_configuration_position_id: string;
  slot_number?: number;
};

export function officialParticipationMatches(events: HistoricalPlayedEvent[]): Set<string> {
  return new Set(events.filter(event => event.participation_event_reason === 'Official result reconciliation').map(event => event.match_event_id));
}

export function authoritativePlayedEvents<T extends HistoricalPlayedEvent>(events: T[]): T[] {
  const official = officialParticipationMatches(events);
  return events.filter(event => !official.has(event.match_event_id) || event.participation_event_reason === 'Official result reconciliation');
}

// Presentation only. Never rewrite the preserved published/planned lineup.
// Official participation events are already team-authorized by the existing RLS.
export function effectiveHistoricalLineup(entries: HistoricalLineupEntry[], events: HistoricalPlayedEvent[]) {
  const official = officialParticipationMatches(events);
  const actual = new Map<string, HistoricalLineupEntry>();
  for (const event of authoritativePlayedEvents(events)) {
    if (!official.has(event.match_event_id) || !event.league_configuration_position_id) continue;
    const key = `${event.match_event_id}:${event.league_configuration_position_id}:${event.player_id}`;
    actual.set(key, { match_event_id: event.match_event_id, player_id: event.player_id, league_configuration_position_id: event.league_configuration_position_id });
  }
  const slots = new Map<string, number>();
  const actualEntries = [...actual.values()].sort((a, b) => a.player_id.localeCompare(b.player_id)).map(entry => {
    const key = `${entry.match_event_id}:${entry.league_configuration_position_id}`;
    const slot = (slots.get(key) || 0) + 1;
    slots.set(key, slot);
    return { ...entry, slot_number: slot };
  });
  return [...entries.filter(entry => !official.has(entry.match_event_id)).map(entry => ({ ...entry, slot_number: entry.slot_number || 1 })), ...actualEntries];
}

export async function loadHistoricalPlayedEvents(matchIds: string[]) {
  if (!matchIds.length) return { data: [] as HistoricalPlayedEvent[], error: null };
  return supabase.from('player_participation_events')
    .select('match_event_id, player_id, league_configuration_position_id, participation_event_reason')
    .in('match_event_id', [...new Set(matchIds)])
    .eq('participation_event_type', 'played').is('deleted_at', null);
}
